import { useState, useCallback, useEffect, useMemo } from "react";
import moment from "moment";
import randomColor from "randomcolor";

import {
  DASH_STYLE,
  INTERVAL_OPTIONS,
  INTERVAL_MAP,
  INTERVAL_THRESHOLDS,
  FORECAST_MONTHS
} from "./metricChartUtil";
import { reformatData } from "../../util/helperFunctions";
import { toast } from "react-toastify";
import { fetchExpressions } from "../../api/sensorApi";
import * as R from "rambda";

export const inferDate = (start, interval, index) => {
  const [mult, momentInterval] = INTERVAL_MAP[interval];
  return moment(start)
    .add(index * mult, momentInterval)
    .format("YYYY-MM-DDTHH:mm:ss");
};

export const checkIntervalOptions = (start, end) => {
  let diff = moment(end).diff(moment(start));
  return INTERVAL_OPTIONS.filter(
    i => !INTERVAL_THRESHOLDS[i.value] || INTERVAL_THRESHOLDS[i.value] > diff
  );
};

export const extractSingleMetric = (metricRes, requestConfig) => {
  return {
    name: requestConfig.name,
    color: requestConfig.color,
    data: metricRes.data.map((d, index) =>
      metricRes.missing[index] ? null : d
    ),
    missing: metricRes.missing,
    dash: metricRes.dash || DASH_STYLE.SOLID,
    type: metricRes.type,
    mode: metricRes.mode,
    start: metricRes.start,
    interval: metricRes.interval,
    tag: requestConfig.tag,
    showlegend: requestConfig.showlegend,
    unit: metricRes.unit.symbol,
    variablemarkers: requestConfig.variablemarkers,
    stackgroup: requestConfig.stackgroup,
    yaxis: requestConfig.yaxis
  };
};

export const extractMetrics = (metricRes, requestConfig) => {
  return R.keys(metricRes.result).flatMap(metricId => {
    return R.keys(metricRes.result[metricId]).map(metricName => {
      return {
        name: requestConfig.labelMapping
          ? requestConfig.labelMapping(metricName, metricId)
          : `${metricName}_${metricId}`,
        color: requestConfig.colorMapping
          ? requestConfig.colorMapping(metricName, metricId)
          : randomColor(`${metricName}_${metricId}`),
        dash: requestConfig.dashMapping
          ? requestConfig.dashMapping(metricName, metricId)
          : DASH_STYLE.SOLID,
        variablemarkers: requestConfig.variableMarkersMapping
          ? requestConfig.variableMarkersMapping(metricName, metricId)
          : false,
        stackgroup: requestConfig.stackgroupMapping
          ? requestConfig.stackgroupMapping(metricName, metricId)
          : null,
        yaxis: requestConfig.yaxisMapping
          ? requestConfig.yaxisMapping(metricName, metricId)
          : null,
        layerorder: requestConfig.layerorderMapping
          ? requestConfig.layerorderMapping(metricName, metricId)
          : null,
        data: metricRes.result[metricId][metricName].data.map((d, index) =>
          metricRes.result[metricId][metricName].missing[index] ? null : d
        ),
        missing: metricRes.result[metricId][metricName].missing,
        type: metricRes.result[metricId][metricName].type,
        mode: metricRes.result[metricId][metricName].mode,
        start: metricRes.result[metricId][metricName].start,
        interval: metricRes.result[metricId][metricName].interval,
        tag: requestConfig.tag,
        showlegend: requestConfig.showlegend,
        unit: metricRes.result[metricId][metricName].unit.symbol,
      };
    });
  });
};

export const useMetricChartHook = ({ initStart, initEnd, initInterval, initForecastMonth }) => {
  const [metricData, setMetricData] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const [scatterData, setScatterData] = useState(null);
  const [scatterDataLoading, setScatterDataLoading] = useState(false);

  const [start, _setStart] = useState(initStart);
  const [end, _setEnd] = useState(initEnd);
  const [interval, _setSelectedInterval] = useState(
    INTERVAL_OPTIONS.find(o => o.value === initInterval)
  );

  const [forecastMonths, _setSelectedMonths] = useState(
    FORECAST_MONTHS.find(o => o.value === initForecastMonth)
  );
  const [intervalOpts, setIntervalOptions] = useState(INTERVAL_OPTIONS);

  const [formDirty, setFormDirty] = useState(false);
  const [requestParams, _setRequestParams] = useState({ start, end, interval });
  const [scaled, _setScaled] = useState(false);
  const forecastMonthsOpts = FORECAST_MONTHS;
  /**
   * Re-evaluate the start, end, and interval currently selected
   * If it lies outside the allowed data thresholds then update the selected interval to
   * a larger grain of data
   */
  useEffect(() => {
    setFormDirty(true);
    const updatedIntervalOpts = checkIntervalOptions(start, end);
    if (updatedIntervalOpts.length !== intervalOpts.length) {
      setIntervalOptions(updatedIntervalOpts);
      _setSelectedInterval(updatedIntervalOpts[updatedIntervalOpts.length - 1]);
    }
  }, [start, end, interval, intervalOpts]);

  useEffect(() => {
    setFormDirty(true);
  }, [forecastMonths]);

  const data = useMemo(() => {
    const md = metricData || [];
    const sd = scatterData || [];
    return md.concat(sd);
  }, [metricData, scatterData]);

  const loading = useMemo(() => scatterDataLoading || metricsLoading, [
    metricsLoading,
    scatterDataLoading
  ]);


  const refreshChartData = useCallback(
    requests => {
      setMetricsLoading(true);
      setMetricData(null);
      if (requests?.length === 0 || requests[0]?.body?.expressions[0] === undefined) {
        setMetricsLoading(false);
        console.log("No requests or expressions found, skipping data fetch");
        return;
      }
      const promises = requests
        .map(r => ({
          ...r,
          body: {
            ...r.body,
            interval: requestParams.interval.value,
            start: requestParams.start.format("YYYY-MM-DD"),
            end: requestParams.end.format("YYYY-MM-DD"),
          }
        }))
        .map(r => fetchExpressions(r.path, r.body));
      Promise.all(promises).then(resp => {

        try {
          if (!resp) {
            setMetricData(null);
          } else {
            console.log(resp)
            resp = reformatData(resp);
            resp = resp.flatMap((metricRes, reqIndex) => {
              if (!metricRes.result) {
                return extractSingleMetric(metricRes, requests[reqIndex]);
              } else {
                const graphMetricData = extractMetrics(metricRes, requests[reqIndex]);
                // const measuredMetricData = graphMetricData.find(metric => metric.name === "Measured wall thickness")?.data;
                // const predictedMetricData = graphMetricData.find(metric => metric.name === "Predicted wall thickness")?.data;
                // if (measuredMetricData && predictedMetricData) {
                //   const ismeasuredMetricData = measuredMetricData.every((val) => val === null);
                //   const ispredictedMetricData = predictedMetricData.every((val) => val === null);
                //   if (ismeasuredMetricData && ispredictedMetricData) {
                //     toast.error("The Prediction results may not have been displayed in the UI for this sensor because there is no last measurement data available to initiate the prediction", {
                //       toastId: "noData",
                //       style: {
                //         textAlign: "center"
                //       }
                //     });
                //   }
                // }
                return graphMetricData;
              }
            });
            if (!resp.length) return [];

            // Creating a list of properties that can be customized for each series.
            // So that, if there are further requests to customize a particular series, they can be done in one place.

            const start = resp[0].start;
            const interval = resp[0].interval;
            const dates = resp[0].data.map((val, i) =>
              inferDate(start, interval, i)
            );
            const countOccurrences = (arr, val) =>
              arr.reduce((a, v) => (v === val ? a + 1 : a), 0);
            const markerSize = (missing) => {
              if (missing === undefined) {
                return "8";
              } else {
                if (
                  countOccurrences(missing, 0) /
                  (countOccurrences(missing, 100) + countOccurrences(missing, 0)) <
                  0.1
                ) {
                  return "8";
                } else {
                  return "6";
                }
              }
            };
            resp = resp.map((obj) => {
              obj.data = obj.data.map((ele) =>
                ele && ele !== null && !isNaN(ele) ? ele.toFixed(3) : ele
              );
              return obj;
            });
            resp = resp.map((d) => ({
              x: dates,
              y: d.data,
              missing: d.missing,
              name: d.name,
              text: `[${d.unit}]`,
              hovertemplate: `%{y} [${d.unit}]`,
              type: d.type,
              mode: d.variablemarkers ? "markers+lines" : d.mode,
              yaxis: d.yaxis,
              line: {
                color: d.color,
                dash: d.dash,
                width: 2,
              },
              marker: {
                size: d.variablemarkers ? markerSize(d.missing) : 0,
              },
              stackgroup: d.stackgroup,
              layerorder: d.layerorder ? d.layerorder : 2,
              tag: d.tag,
              showlegend: d.showlegend,
            }));
            resp.sort((b, a) => {
              return a.layerorder - b.layerorder;
            });
            setMetricData(resp);
          }
        } catch (err) {
          console.log("err", err);
          setMetricData(null);
          setMetricsLoading(false);
        }

        setMetricsLoading(false);
      }).catch((error) => {
        console.log(error);
        toast.error("Error fetching data. Please try again later.");
      });
    },
    [requestParams]
  );

  const refreshScatterData = useCallback(
    requests => {
      if (R.any(obj => !obj.startField || !obj.endField, requests)) {
        throw new Error(
          `Scatter data not configured correctly! All scatter plot requests must have a startField and endField configured`
        );
      }

      setScatterDataLoading(true);
      setScatterData(null);
      const promises = requests.map(req => {
        const filterVal = R.path(["c3args", "spec", "filter"], req),
          dateRangeFilter = `${req.startField} >= "${requestParams.start.format(
            "YYYY-MM-DD"
          )
            } " && ${req.endField} < "${requestParams.end.format("YYYY-MM-DD")} "`,
          filter = R.filter(R.identity, [filterVal, dateRangeFilter]).join(
            " && "
          );

        const args = {
          ...req.c3args,
          spec: {
            ...req.c3args.spec,
            filter
          }
        };
        //Adding extra filter if fetch is for ModelAlert, to avoid incorrect alerts
        if (req.c3type === "ModelAlert") {
          if (args.spec.filter && args.spec.filter.length) {
            args.spec.filter +=
              " && (!exists(category) || category!='INCORRECT_PREDICTION')";
          } else {
            args.spec.filter =
              "(!exists(category) || category!='INCORRECT_PREDICTION')";
          }
        }

        return null;
      });

      Promise.all(promises).then(resp => {
        let noPredictionAlerts = [];
        let outOfBoundAlerts = [];
        let alertsRequestObj;
        resp = resp.map((fetchResult, reqIndex) => {
          //if the result is for alerts, we are pulling out the alerts by their categories.
          // We create seperate series for each alert category.
          if (requests[reqIndex].id.toLowerCase() === "alerts") {
            noPredictionAlerts = R.pathOr([], ["objs"], fetchResult).filter(
              alert => {
                return alert.category === "NO_PREDICTION";
              }
            );
            outOfBoundAlerts = R.pathOr([], ["objs"], fetchResult).filter(
              alert => {
                return alert.category === "OUT_OF_BOUNDS";
              }
            );
            alertsRequestObj = { ...requests[reqIndex] };
          }
          const fetchData = {
            x: [],
            y: [],
            text: [],
            name: requests[reqIndex].name,
            mode: "markers",
            type: "scatter",
            yaxis: requests[reqIndex].yaxis,
            marker: {
              color:
                requests[reqIndex].color ||
                randomColor(
                  `${requests[reqIndex].c3type}_${requests[reqIndex].c3function}`
                )
            }
          };
          if (requests[reqIndex].id.toLowerCase() === "alerts") {
            fetchData.name = "Deviation";
            fetchData["hovertemplate"] =
              "<i>Category</i>: Deviation<br><i>Status</i>: %{text}<extra></extra>";
          }

          const timeField = requests[reqIndex].startField;
          const textField = requests[reqIndex].textField;

          R.pathOr([], ["objs"], fetchResult).forEach(obj => {
            if (
              requests[reqIndex].id.toLowerCase() === "alerts" &&
              obj.category !== "DEVIATION"
            ) {
              return false;
            }
            fetchData.x.push(
              moment(obj[timeField]).format("YYYY-MM-DDTHH:mm:ss")
            );
            fetchData.y.push(1);
            fetchData.text.push(R.path(textField, obj));
          });

          return fetchData;
        });
        if (noPredictionAlerts.length) {
          let noPredictionAlertsData = {
            x: [],
            y: [],
            text: [],
            name: "No Prediction",
            mode: "markers",
            type: "scatter",
            yaxis: alertsRequestObj.yaxis,
            marker: {
              symbol: "diamond",
              color: "#55aa55"
            },
            hovertemplate:
              "<i>Category</i>: No Prediction<br><i>Status</i>: %{text}<extra></extra>"
          };
          const timeField = alertsRequestObj.startField;
          const textField = alertsRequestObj.textField;
          noPredictionAlerts.forEach(alert => {
            noPredictionAlertsData.x.push(
              moment(alert[timeField]).format("YYYY-MM-DDTHH:mm:ss")
            );
            noPredictionAlertsData.y.push(1);
            noPredictionAlertsData.text.push(R.path(textField, alert));
          });
          resp.push(noPredictionAlertsData);
          if (outOfBoundAlerts.length) {
            let outOfBoundAlertsData = {
              x: [],
              y: [],
              text: [],
              name: "Out of Bounds",
              mode: "markers",
              type: "scatter",
              yaxis: alertsRequestObj.yaxis,
              marker: {
                symbol: "square",
                color: "#FFA500"
              },
              hovertemplate:
                "<i>Category</i>: Out of Bounds<br><i>Status</i>: %{text}<extra></extra>"
            };
            const timeField = alertsRequestObj.startField;
            const textField = alertsRequestObj.textField;
            outOfBoundAlerts.forEach(alert => {
              outOfBoundAlertsData.x.push(
                moment(alert[timeField]).format("YYYY-MM-DDTHH:mm:ss")
              );
              outOfBoundAlertsData.y.push(1);
              outOfBoundAlertsData.text.push(R.path(textField, alert));
            });
            resp.push(outOfBoundAlertsData);
          }
        }

        setScatterData(resp);
        setScatterDataLoading(false);
      });
    },
    [requestParams.start, requestParams.end]
  );

  const setStart = val => {
    _setStart(moment(val));
  };

  const setEnd = val => {
    _setEnd(moment(val));
  };

  const setSelectedInterval = val => {
    if (val.value) {
      _setSelectedInterval(val);
    } else {
      const valueObj = INTERVAL_OPTIONS.find(o => o.value === val);
      _setSelectedInterval(valueObj);
    }
  };

  const setSelectedMonths = val => {

    if (val.value) {
      _setSelectedMonths(val);
    } else {
      const valueObj = FORECAST_MONTHS.find(o => o.value === +val);
      _setSelectedMonths(valueObj);
    }
  };

  const setRequestParams = params => {
    setFormDirty(false);
    console.log(params)
    _setRequestParams(params);
  };

  const setScaled = val => {
    _setScaled(val);
  };

  return {
    data,
    metricData,
    scatterData,
    loading,
    start,
    setStart,
    end,
    setEnd,
    interval,
    setSelectedInterval,
    intervalOpts,
    forecastMonths,
    setSelectedMonths,
    forecastMonthsOpts,
    requestParams,
    setRequestParams,
    formDirty,
    refreshChartData,
    refreshScatterData,
    scaled,
    setScaled
  };
};
